// import

import { cloudinaryCfg } from "~/config";
import type { CmsCollection, CmsField, CmsMediaLibrary, Widget } from "./decapTypes";
import { pathSlug } from "./fields";

// types

type NoChange = 'folder' | 'format' | 'extension' | 'frontmatter_delimiter' | 'create' | 'delete';

export type FolderOpts = 
  Omit<CmsCollection, NoChange> & 
  Pick<Required<CmsCollection>, 'label_singular' | 'fields'>;
export type FolderCollection = 
  Omit<CmsCollection, 'files'> & 
  Pick<Required<CmsCollection>, 'folder'>;

export type NestedOpts = FolderOpts;
export type NestedCollection = FolderCollection & 
  Pick<Required<CmsCollection>, 'meta' | 'nested'>;

export type FieldOpts<T extends Widget> =
  Omit<CmsField & {widget: T}, 'widget'>;
export type Field<T extends Widget> =
  CmsField & {widget: T};

export type MediaLibraryOpts = 
  Omit<CmsMediaLibrary, 'name'>;

// fns

export const createMediaLibrary = (opts: MediaLibraryOpts = {}): CmsMediaLibrary => {
  return {
    name: 'cloudinary',
    // @ts-expect-error
    output_filename_only: false,
    use_transformations: false,
    use_secure_url: true,
    ...opts,
    
    config: {
      cloud_name: cloudinaryCfg.name,
      api_key: cloudinaryCfg.key,
      ...opts.config,
    },
  } satisfies CmsMediaLibrary;
};

export const createFolder = (opts: FolderOpts): FolderCollection => {
  return {
    // force
    create: true,
    delete: true,
    extension: 'md',
    folder: `src/content/${opts.name}`,
    format: 'yaml-frontmatter',
    frontmatter_delimiter: '---',
    
    // defaults
    identifier_field: 'title',
    preview_path: `${opts.name}/{{slug}}`,
    slug: '{{slug}}',
    sortable_fields: ['title', 'publishDate', 'updateDate'],
    summary: '{{title}}',
  
    // overrides
    ...opts,
  };
};

export const createNested = (opts: NestedOpts): NestedCollection => {
  const folder = createFolder(opts);

  return {
    ...folder,

    nested: {depth: 5},
    meta: {
      path: {
        widget: 'string', 
        label: 'Path', 
        index_file: 'index',
        // @ts-expect-error
        pattern: pathSlug?.pattern
      },
    },
  };
};

export const createLayout = (value: string): {
  filter: {field: string; value: unknown};
  field: CmsField;
} => {
  return {
    filter: {field: 'layout', value},
    field: {name: 'layout', widget: 'hidden', default: value},
  };
};

export const createBoolean = (opts: FieldOpts<'boolean'>): Field<'boolean'> => {
  return {
    widget: 'boolean',
    ...opts,
  };
};

export const createCode = (opts: FieldOpts<'code'>): Field<'code'> => {
  return {
    widget: 'code',
    ...opts,
  };
};

export const createColor = (opts: FieldOpts<'color'>): Field<'color'> => {
  return {
    widget: 'color',
    ...opts,
  };
};

export const createDatetime = (opts: FieldOpts<'datetime'>): Field<'datetime'> => {
  return {
    widget: 'datetime',
    default: '',
    ...opts,
  };
};

export const createDate = (opts: FieldOpts<'datetime'>): Field<'datetime'> => {
  return createDatetime({
    format: 'YYYY-MM-DD', 
    date_format: 'YYYY-MM-DD', 
    time_format: false,
    ...opts,
  });
};

export const createTime = (opts: FieldOpts<'datetime'>): Field<'datetime'> => {
  return createDatetime({
    format: 'hh:mm aa',
    date_format: false,
    time_format: 'hh:mm aa',
    ...opts,
  });
};

export const createFile = (opts: FieldOpts<'file'>): Field<'file'> => {
  return {
    widget: 'file',
    ...opts,
  };
};

export const createImage = (opts: FieldOpts<'image'>): Field<'image'> => {
  return {
    widget: 'image',
    ...opts,
  };
};

export const createHidden = (opts: FieldOpts<'hidden'>): Field<'hidden'> => {
  return {
    widget: 'hidden',
    ...opts,
  };
};

export const createList = (opts: FieldOpts<'list'>): Field<'list'> => {
  return {
    widget: 'list',
    ...opts,
  };
};

export const createMap = (opts: FieldOpts<'map'>): Field<'map'> => {
  return {
    widget: 'map',
    ...opts,
  };
};

export const createMark = (opts: FieldOpts<'markdown'>): Field<'markdown'> => {
  return {
    widget: 'markdown',
    ...opts,
  };
};

export const createLineMark = (opts: FieldOpts<'markdown'>): Field<'markdown'> => {
  return {
    widget: 'markdown',
    ...opts,
    minimal: true,
    buttons: ['bold', 'italic', 'code', 'link'],
    editor_components: [],
    pattern: ['^[^\\n]*$', 'No line breaks'],
  };
};

export const createParaMark = (opts: FieldOpts<'markdown'>): Field<'markdown'> => {
  return {
    widget: 'markdown',
    ...opts,
    minimal: true,
    buttons: ['bold', 'italic', 'code', 'link'],
    editor_components: [],
    pattern: [
      '^(?!.*\\n\\s*\\n)[^\\n]*(?:\\n[^\\n]+)*$', 
      'No paragraph breaks',
    ],
  };
};

export const createNumber = (opts: FieldOpts<'number'>): Field<'number'> => {
  return {
    widget: 'number',
    ...opts,
  };
};

export const createObject = (opts: FieldOpts<'object'>): Field<'object'> => {
  return {
    widget: 'object',
    ...opts,
  };
};

export const createFold = (opts: FieldOpts<'object'>): Field<'object'> => {
  return {
    widget: 'object',
    collapsed: true,
    ...opts,
  };
};

export const createRelation = (opts: FieldOpts<'relation'>): Field<'relation'> => {
  return {
    widget: 'relation',
    ...opts,
  };
};

export const createSelect = (opts: FieldOpts<'select'>): Field<'select'> => {
  return {
    widget: 'select',
    ...opts,
  };
};

export const createString = (opts: FieldOpts<'string'>): Field<'string'> => {
  return {
    widget: 'string',
    ...opts,
  };
};

export const createText = (opts: FieldOpts<'text'>): Field<'text'> => {
  return {
    widget: 'text',
    ...opts,
  };
};

export const required = <T extends Widget>(field: Field<T>): Field<T> & {required: true} => {
  return {...field, required: true};
}

export const optional = <T extends Widget>(field: Field<T>): Field<T> & {required: false} => {
  return {...field, required: false};
}